<template>
  <div
    dir="ltr"
    class="tournament-grid-wrapper position-relative d-flex flex-column"
    :class="[
      { 'head-stucked': isHeadSticky, 'h-100': !isBattleRoyale },
      grid ? `is-${grid}` : '',
    ]"
  >
    <div
      v-if="isLoading && !rounds.length && !isLowerGrid"
      class="d-flex align-items-center h-100"
    >
      <HeraDummyList icon-name="dashboard" position="center">
        <span class="hera-muted">{{
          $t('_web_stage_grid_fetching_data', 'Fetching data ...')
        }}</span>
      </HeraDummyList>
    </div>
    <div
      v-if="!isLoading && !rounds.length && !isLowerGrid"
      class="d-flex align-items-center h-100"
    >
      <HeraDummyList
        size="200"
        :icon-name="`dummy/tournament-grids/${blockType}`"
        position="center"
        class="my-5"
        :header-text="
          $t(
            '_web_tournament-participants_not_drawn',
            'Brackets are not ready.'
          )
        "
        :text="
          $t(
            '_web_tournament-participants_not_drawn-description',
            'Once Registration closes you can qualify participants and build brackets.'
          )
        "
      >
        <div slot="ctaButton">
          <Btn
            class="btn-accent-simple mb-4"
            :label="$t('_web_tournament_go-to-stage-edit', 'Setup stage')"
            @click.stop="goToBracketsSettings"
          />
        </div>
      </HeraDummyList>
    </div>
    <div
      v-else
      ref="gridHead"
      class="sticky-top px-0 flex-shrink-0 grid-table-header-wrapper"
      :class="isBattleRoyale ? 'is-invisible' : isLowerGrid ? 'pt-0' : 'pt-3'"
    >
      <ScrollSync
        :key="roundsKey"
        proportional
        horizontal
        class="custom-scrollbar scrollbars-hide grid-table-header"
        :style="{ 'z-index': stickyZIndex, opacity: isBattleRoyale ? 0 : null }"
      >
        <div class="row flex-nowrap grid-table-header mx-0 no-gutters">
          <div
            v-for="(round, roundIndex) in rounds"
            :key="`round-${roundIndex}-head`"
            ref="roundMatchesHead"
            class="col round-step d-flex flex-column border-right border-dark"
            :class="{ 'is-empty': !round.matches }"
            :style="{ height: `${headerHeight}px` }"
          >
            <div
              v-observe-visibility="{
                callback: roundVisibilityChanged,
                intersection: {
                  threshold: 0.1,
                },
              }"
              class="d-flex flex-column position-relative px-4"
              :data-round-index="roundIndex"
            >
              <template v-if="!isBattleRoyale && round.matches">
                <Subheader
                  class="m-0 text-nowrap d-block"
                  :text="roundName(rounds, roundIndex)"
                />
                <div
                  class="d-flex justify-content-start align-items-center"
                  :dir="currentDir"
                >
                  <icon
                    name="calendar"
                    width="20"
                    :fill="$scss.colors.secondaryTextBg"
                    class="h-mr-3"
                  />
                  <TextBlock
                    :size="0.75"
                    :variant="'secondary'"
                    :line-height="1"
                    >{{
                      round.date_start | localDate($times.DATE_SUBMED)
                    }},</TextBlock
                  >
                  <TextBlock
                    class="h-ml-3"
                    :size="0.75"
                    :variant="'secondary'"
                    :line-height="1"
                    >{{
                      round.date_start | localDate($times.TIME_SIMPLE)
                    }}</TextBlock
                  >
                </div>
              </template>
            </div>
          </div>
        </div>
      </ScrollSync>
    </div>
    <div
      ref="gridWrapper"
      class="grid-table-wrapper flex-grow-1 position-relative"
    >
      <Popover
        v-if="matchCardDetailsComponent && isAdmin"
        :position="activeMatchPosition"
        :closest="$refs.gridWrapper"
        :float="['center']"
        regard="over"
        class="float-dropbox--transparent-wrapper active-match-wrapper mt-0"
        :ignore-clicks-on="['.mx-datepicker-popup', '.grid-popup-menu']"
        @toggle="onActiveMatchToggle"
      >
        <component
          :is="matchCardDetailsComponent"
          v-if="activeMatchID"
          ref="activeMatch"
          :key="`${activeMatchConfig.key}--active`"
          :class="activeMatchConfig.class"
          v-bind="{ ...activeMatchConfig.props, isActive: true }"
          v-on="activeMatchConfig.on"
        />
      </Popover>
      <PopoverMenu
        v-if="isAdmin"
        ref="popupMenu"
        :position="optionsPosition"
        :closest="
          isMenuInHeader && isHeadSticky
            ? $data.$documentElement
            : $refs.gridWrapper
        "
        :is-fixed="isMenuInHeader && isHeadSticky"
        :float="['top', isMenuInHeader ? 'left' : 'right']"
        :items="isAddingPlayer ? filteredUsersForAdding : popoverItems"
        :auto-close="!needToPickPlayer"
        ignore-clicks-on=".modalbackdrop .hera-btn"
        :before-close-callback="preventAddPlayerInterruption"
        class="grid-popup-menu"
        @toggle="onMenuToggle"
      >
        <template slot="header">
          <SearchField
            v-model="addPlayerSearch"
            class="mx-4 mt-4 mb-3 w-auto"
            :placeholder="
              $t(
                '_web_tournament-management-grid_find-players-to-add',
                'Find players'
              )
            "
          />
        </template>
        <template
          v-if="isAddingPlayer && !filteredUsersForAdding.length"
          slot="footer"
        >
          <TextBlock
            v-if="!addPlayerSearch"
            class="mx-4 my-3"
            tag="div"
            variant="secondary"
            >{{
              $t(
                '_web_tournament-management-grid_no-players-to-add',
                'No players for adding'
              )
            }}</TextBlock
          >
          <TextBlock v-else class="mx-4 my-3" tag="div" variant="secondary">{{
            $t(
              '_web_tournament-management-grid_no-players-to-add-filtered',
              'No players for adding, refine your search, please.'
            )
          }}</TextBlock>
        </template>
        <template slot-scope="{ item }">
          <TextBlock
            v-if="item.user_id"
            variant="primary"
            :class="{ disabled: item.disabled }"
            class="px-4 py-3 d-flex align-items-center"
            @click.native.stop="onPlayerPickedForAdding(item)"
          >
            <template v-if="item.icon">
              <Avatar
                :size="18"
                :type="item.player_type"
                :img-url="item.avatar"
                avatar-type="normalPlayer"
                :background-fill="$scss.colors.darkBg"
              />
              <span class="dummy-separator p-0 m-0 ml-2"></span>
            </template>
            <span class="py-2">
              {{ item.user_name || item.user_id }}
            </span>
          </TextBlock>
          <TextBlock
            v-else
            :variant="getMenuItemVariant(item)"
            :class="{ disabled: item.disabled }"
            class="px-4 py-3 d-flex align-items-center"
            @click.native="executeMenuItemHandler(item)"
          >
            <template v-if="item.icon">
              <icon
                :name="item.icon"
                width="14"
                :fill="getMenuItemFill(item)"
              />
              <span class="dummy-separator p-0 m-0 ml-2"></span>
            </template>
            <span class="py-2">
              {{
                $t(
                  `_web_tournament_match-menu-item-${item.key}`,
                  item.title,
                  item.props
                )
              }}
            </span>
          </TextBlock>
        </template>
      </PopoverMenu>
      <ScrollSync
        proportional
        horizontal
        class="custom-scrollbar grid-table-body h-100"
      >
        <VirtualScroll
          v-if="rounds.length && $refs.roundMatchesHead"
          ref="grid"
          :key="gridKey"
          v-bind="{
            itemClass: `col round-step flex-grow-1 matches-in-the-round
               ${isBattleRoyale ? 'px-3' : 'px-0 border-right'}
               ${isLowerGrid || isBattleRoyale ? 'mb-3' : 'my-3'}`,
            wrapperClass:
              'row flex-nowrap ' + `${isBattleRoyale ? 'mx-n3' : 'mx-0'} `,
            cacheCount: getRoundCacheCount,
            debounce: 250,
            item: getItem,
            itemHeight: getItemHeight,
            visibleCount: getRoundVisibleCount,
            itemsCount: getItemsCount,
            itemProps: getMatchProps,
            instanceCount: rounds.length,
            multipleMode: true,
            spyScrollXOn: '.grid-table-body',
            hasXScroll: true,
            instanceWidth: $refs.roundMatchesHead[0].offsetWidth,
          }"
          @scroll="onScrollStop"
        />
      </ScrollSync>
      <!-- <ResultsModal
        v-if="isAdmin && showResultsModal && editResultsData.id"
        v-model="showResultsModal"
        :edit="editResultsMode"
        :match-data="editResultsData"
        @resolved="saveEditResults"
      /> -->
    </div>
  </div>
</template>

<script>
//utils
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
//mixins
import isLoading from '@/mixins/isLoading';
import matchHelper from '@/views/tournaments/management/brackets/mixins/matchHelper';
import roundHelper from '@/views/tournaments/management/brackets/mixins/roundHelper';
import matchMenu from '@/views/tournaments/management/brackets/mixins/matchMenu';
import roundMenu from '@/views/tournaments/management/brackets/mixins/roundMenu';
import dateMethods from '@/mixins/dateMethods';
//components
import Panel from '@/components/atoms/Panel';
import ScrollSync from 'vue-scroll-sync';
import PopoverMenu from '@/components/popover/Menu';
import Popover from '@/components/popover/index.vue';
import TextBlock from '@/components/atoms/TextBlock';
import Subheader from '@/components/atoms/Subheader';
import Title from '@/components/atoms/Title';
import icon from '@/components/atoms/Icon';
import SearchField from '@/components/molecules/form/HeraSearchInput';
import VirtualScroll from '@/components/common/VirtualScroll';
import Btn from '@/components/atoms/HeraFormBtn';
import HeraDummyList from '@/components/common/HeraDummyList';
import { displayName } from '@/filters/displayName';
import { prop } from '@/utils/factories';
// const ResultsModal = () => import('@/components/tournaments/ResultsModal');
import DateTimePicker from '@/components/form/DateTimePicker';
import GoBackBtn from '@/components/common/GoBackBtn.vue';
import {
  STAGE_STATUSES,
  COMPETITION_STATUSES,
  BLOCK_TYPES,
  CONSTRUCTOR_MODES,
} from '@/constants/tournaments';

const PLAYOFF_GRID_HEADER_HEIGHT = 60;

export default {
  name: 'Grid',
  components: {
    Btn,
    Panel,
    ScrollSync,
    PopoverMenu,
    Popover,
    TextBlock,
    Subheader,
    icon,
    // ResultsModal,
    VirtualScroll,
    HeraDummyList,
    DateTimePicker,
    Title,
    SearchField,
    GoBackBtn,
  },
  mixins: [
    ...dateMethods,
    isLoading,
    matchHelper,
    roundHelper,
    matchMenu,
    roundMenu,
  ],
  props: {
    roundName: prop(Function),
    getRoundItemHeight: prop(Function, height => () => height),
    getRoundVisibleCountBase: prop(Function, () => 1),
    getNextMatchIndex: prop(Function, i => i),
    matchProps: prop(Function, () => ({
      props: {},
      on: {},
    })),
    autoHeight: prop(Boolean),
    isCollapsible: prop(Boolean),
    isBattleRoyale: prop(Boolean),
    isAdmin: prop(Boolean),
    matchCardComponent: prop([Object, Function], null),
    matchCardDetailsComponent: prop([Object, Function], null),
    grid: prop(String),
  },
  data() {
    return {
      firstVisibleMatch: -1,
      firstVisibleRound: -1,
      checkInStatuses: {},
      rounds: [],
      addPlayerSearch: '',
      matchRounds: 1,
      forceUpdate: false,
      isCollapsed: false,
      matches: [],
      isAddingPlayer: false,
      roundItemHeight: this.autoHeight
        ? 0
        : parseInt(this.$scss.matchItemHeight),
      // todo suffer!
      // if headerHeight === 0 -> is invisible -> this.roundsUpdateQuery will not work
      // use headerHeight 1px
      headerHeight: this.isBattleRoyale ? 1 : PLAYOFF_GRID_HEADER_HEIGHT,
      pageSize: this.autoHeight ? 1 : 4,
      optionsPosition: null,
      optionsData: null,
      popoverItems: null,
      stickyZIndex: 1000,
      isMenuInHeader: false,
      isHeadSticky: false,
      $documentElement: document.documentElement,
      $body: document.body,
      matchResults: {},
      //Results match modal
      showResultsModal: false,
      editResultsMode: false,
      editResultsData: {},
      savingResults: null,
      roundsUpdateQuery: new Subject(),
      visibleInstanceCount: 0,
      getItemHeight: null,
      activeMatchID: null,
      activeMatchIndex: null,
      activeMatchRoundIndex: null,
      activeMatchPosition: null,
      activeMatchConfig: {},
      needToPickPlayer: false,
    };
  },
  computed: {
    ...mapGetters([
      'isStarted',
      'hasGroupStage',
      'currentTournamentStage',
      'isTournamentFieldDisabled',
      'isStageFieldDisabled',
      'isCompetitionFieldDisabled',
      'currentTournamentBlock',
      'currentLocale',
      'isSwapping',
      'swapData',
      'hasExternalSwapSource',
      'hasErrorOnSwap',
    ]),
    stageID() {
      return this.$route.params.stageID;
    },
    currentDir() {
      return (this.currentLocale && this.currentLocale.direction) || 'ltr';
    },
    competitionID() {
      return this.$route.params.blockID;
    },

    tournamentID() {
      return this.$route.params.tournamentID;
    },
    hasCheckIn() {
      return this.$lodash.get(
        this.currentTournamentStage,
        'check_in_enabled',
        false
      );
    },
    needCheckInInfo() {
      return (
        this.hasCheckIn &&
        this.$route.params.constructorMode === CONSTRUCTOR_MODES.PUBLISH
      );
    },
    blockType() {
      return (
        this.$lodash.get(
          this.currentTournamentStage,
          'block_configuration.type'
        ) || null
      );
    },
    isSingle() {
      return this.blockType === BLOCK_TYPES.SINGLE;
    },
    isSwiss() {
      return this.blockType === BLOCK_TYPES.SWISS;
    },
    isRR() {
      return this.blockType === BLOCK_TYPES.RR;
    },
    isStageDraft() {
      return (
        this.$lodash.get(this.currentTournamentStage, 'status', '') === 'draft'
      );
    },
    userMenuItems() {
      return [
        {
          key: 'invite-player',
          title: 'Invite to my tournament',
          handler: this.openInviteModal,
          disabled: false,
        },
        {
          key: 'message-with-user',
          title: 'Send Message',
          handler: this.startOneToOneChat,
          disabled: false,
        },
      ];
    },
    isLowerGrid() {
      return this.grid === 'lower';
    },
    canChangeRoundDate() {
      return (
        this.isAdmin &&
        !this.isTournamentFieldDisabled('round_date') &&
        !this.isStageFieldDisabled('round_date') &&
        !this.isCompetitionFieldDisabled(
          'round_date',
          this.currentTournamentBlock
        )
      );
    },
    filteredUsersForAdding() {
      return (
        (this.addPlayerSearch
          ? this.popoverItems.filter(
              item =>
                `${item.user_name}${item.user_id}`
                  .toLowerCase()
                  .indexOf(this.addPlayerSearch.toLowerCase()) > -1
            )
          : this.popoverItems) || []
      );
    },
  },
  watch: {
    hasExternalSwapSource(newVal, oldVal) {
      if (newVal && !oldVal && this.grid === this.swapData.from.grid) {
        this.$options.swapSourceBackup = { ...this.swapData.from };
        this.updatePlayerInMatch(
          this.swapData.from.roundIndex,
          this.swapData.from.matchIndex,
          this.swapData.from.playerIndex,
          this.swapData.to.player
        );
        this.$refs.grid.forceRender();
      } else if (
        !newVal &&
        oldVal &&
        this.$lodash.isEmpty(this.swapData.from) &&
        this.$lodash.isEmpty(this.swapData.to)
      ) {
        if (this.hasErrorOnSwap) {
          this.updatePlayerInMatch(
            this.$options.swapSourceBackup.roundIndex,
            this.$options.swapSourceBackup.matchIndex,
            this.$options.swapSourceBackup.playerIndex,
            this.$options.swapSourceBackup.player
          );
          this.$options.swapSourceBackup = null;
          this.setHasErrorOnSwap(false);
        }
      }
    },
  },
  created() {
    if (!this.autoHeight) {
      document.body.classList.add('has-scrollbar'); // add scrollbar
    }

    if (this.stageID && this.currentTournamentStage.id !== this.stageID) {
      this.setCurrentTournamentStage(this.stageID);
    }
    if (this.tournamentID) {
      this.setLoading();
      this.fetchRoundsData()
        .then(() => this.$emit('fetched', true))
        .catch(this.errorNotify)
        .then(this.unsetLoading);
    }
    this.$options.$roundNodes = [];
    this.roundsUpdateQuery.pipe(debounceTime(500)).subscribe(() => {
      const firstVisibleRound = this.rounds.findIndex(
        ({ isVisible, matches }) => isVisible && matches
      );
      this.firstVisibleRound = Math.max(0, firstVisibleRound);
      this.visibleInstanceCount = this.rounds.filter(
        ({ isVisible }) => isVisible
      ).length;
      this.fetchMatchesData();
    });
  },
  destroyed() {
    if (!this.autoHeight) {
      document.body.classList.remove('has-scrollbar'); // remove class with scrollbar
    }
  },

  mounted() {
    const viewportHeight = document.documentElement.clientHeight;
    const $navbar = document.getElementById('nav');
    const $actionButtons = document.getElementById('breadcrumbsWrapper');
    let gridVisibleHeight = viewportHeight - this.headerHeight;
    let gridHeadTopOffset = 0;
    if ($navbar) {
      gridHeadTopOffset += $navbar.clientHeight;
      gridVisibleHeight -= $navbar.clientHeight;
    }
    if ($actionButtons) {
      gridHeadTopOffset += $actionButtons.clientHeight;
      gridVisibleHeight -= $actionButtons.clientHeight;
    }
    this.$refs.gridHead.style.top = `${gridHeadTopOffset}px`;
    const visibleCount = Math.ceil(gridVisibleHeight / this.roundItemHeight);
    this.pageSize = visibleCount * 2;
  },

  methods: {
    ...mapMutations([
      'openConversationInSliderChatEditor',
      'sliderChatToggleLoader',
      'toggleSliderChat',
      'setCurrentTournamentStage',
      'clearSwapData',
      'setIsSwapping',
      'setSwapTo',
      'setSwapFrom',
      'setHasErrorOnSwap',
    ]),
    ...mapActions([
      'getCompetitionRounds',
      'getCompetitionMatches',
      'swapPlayers',
      //system
      'errorNotify',
      //other
      'createTournamentConversationBySignature',
      'openModal',
      'closeModal',
      'startTournamentChat',
      'fetchTournamentPlayers',
      'deletePlayerFromMatch',
      'fetchPlayersForAdding',
      'addPlayerToMatch',
    ]),
    goToBracketsSettings() {
      this.$router.push({
        name: 'multiStage',
        params: {
          ...this.$route.params,
          constructorMode: CONSTRUCTOR_MODES.CONSTRUCT,
        },
      });
    },
    setActiveMatchConfig() {
      this.activeMatchConfig = this.activeMatchID
        ? this.getMatchProps(this.activeMatchIndex, this.activeMatchRoundIndex)
        : {};
    },
    getItem(index) {
      const { matchCardComponent } = this;
      return this.$lodash.isFunction(matchCardComponent)
        ? matchCardComponent(this.rounds.length)(index)
        : matchCardComponent;
    },
    getItemsCount(roundIndex) {
      return (
        (this.$lodash.isEmpty(this.rounds[roundIndex])
          ? 0
          : this.rounds[roundIndex].matches) ||
        this.getItemsCountHelper(roundIndex)
      );
    },
    getItemsCountHelper(roundIndex) {
      if (this.grid === 'upper') {
        return this.rounds[roundIndex + 1].matches || 0;
      }

      return 0;
    },
    getRoundVisibleCount(roundIndex) {
      return Math.min(
        this.getRoundVisibleCountBase(
          roundIndex,
          this.pageSize,
          this.rounds.length
        ),
        +this.$lodash.get(this.rounds, `[${roundIndex}].matches`) || Infinity
      );
    },
    getRoundCacheCount(roundIndex) {
      return this.getRoundVisibleCount(roundIndex);
    },
    getMatchProps(matchIndex, roundIndex = 0) {
      const match =
        this.$lodash.get(this, `matches[${roundIndex}][${matchIndex}]`) || {};
      const duration = match.date_end - match.date_start;
      const specificProps = this.matchProps({
        roundIndex,
        matchIndex,
        roundsCount: this.rounds.length,
      });
      const matchProps = {
        key: `round-${roundIndex}-match-${matchIndex}-wrapper`,
        props: {
          isSingle: this.isSingle,
          roundIndex,
          roundName: this.roundName(this.rounds, roundIndex),
          matchIndex,
          match,
          prevRoundEnd: this.getNowSeconds(),
          isSwapTarget:
            this.isSwapping && match.id !== this.swapData.from.matchId,
          isSwapSource:
            this.isSwapping && match.id === this.swapData.from.matchId,
          isSwapping: this.isSwapping,
          duration: duration || 0,
          canDeletePlayers:
            this.isAdmin &&
            !this.isRR &&
            // this.isStageDraft &&
            !this.isTournamentFieldDisabled('delete_players') &&
            !this.isCompetitionFieldDisabled(
              'delete_players',
              this.currentTournamentBlock
            ) &&
            this.$route.params.constructorMode === CONSTRUCTOR_MODES.PUBLISH,
          canChangeMatchDate:
            this.isAdmin &&
            this.canChangeMatchDate(match.status) &&
            !this.isTournamentFieldDisabled('match_date'),
          canChangeMatchDuration:
            this.isAdmin && this.canChangeMatchDuration(match.status),
          thirdPlace: this.isThirdPlaceMatch(match),
          hasNotPrevRound:
            roundIndex === 0 || (this.isLowerGrid && roundIndex === 1),
          hasNotNextRound: roundIndex === this.rounds.length - 1,
          isActive: match.id === this.activeMatchID,

          ...(this.isBattleRoyale
            ? {
                editResultsData: this.editResultsData,
                isEdit:
                  this.editResultsMode &&
                  this.editResultsData.round === roundIndex,
                isLoading: this.savingResults === roundIndex,
                matchActions: this.getMatchActions(match).map(item => ({
                  ...item,
                  handler: () => {
                    this.optionsData = {
                      ...(item.key === 'schedule-match'
                        ? {
                            match,
                            canChangeMatchDate:
                              this.isAdmin &&
                              this.canChangeMatchDate(match.status) &&
                              !this.isTournamentFieldDisabled('match_date'),
                            canChangeMatchDuration:
                              this.isAdmin &&
                              this.canChangeMatchDuration(match.status),
                            minDate: this.getNowSeconds(),
                          }
                        : match),
                    };
                    this.executeMenuItemHandler(item);
                  },
                })),
              }
            : null),
        },
        class: ['position-relative'],
        on: {
          startSwap: playerIndex =>
            this.onStartSwap(roundIndex, matchIndex, playerIndex),
          endSwap: playerIndex =>
            this.onEndSwap(roundIndex, matchIndex, playerIndex),
          playerDeleted: (payload = {}) =>
            this.onDeletePlayer({ ...payload, roundIndex, matchIndex }),
          swapCancelled: this.onCancelSwap,
          dateChanged: seconds => this.onMatchDateChanged(match, seconds),
          dateEndChanged: seconds =>
            this.onMatchDateChanged(match, seconds, 'date_end'),
          durationChanged: timestamp =>
            this.onMatchDurationChanged(match, timestamp),
          toggleMenu: event =>
            this.toggleMatchMenu(event, {
              ...match,
              roundIndex,
              matchIndex,
            }),
          addPlayer: (payload = {}) =>
            this.onAddNewPlayer({
              ...payload,
              roundIndex,
              matchIndex,
              matchID: match.id,
            }),
          userMenuOpen: event => this.onOpenUserMenu(event, match),
          setActiveCard: event =>
            this.setActiveCard(event, {
              id: match.id,
              roundIndex,
              matchIndex,
            }),

          ...(this.isBattleRoyale
            ? {
                saveResult: () => this.saveEditResults(this.editResultsData),
                resetResult: () => this.resetEditResults(),
                forceUpdate: () => this.updateMatchesData(),
              }
            : null),
        },
      };
      if (specificProps.props) {
        matchProps.props = {
          ...matchProps.props,
          ...specificProps.props,
          canSwapPlayers:
            this.isAdmin &&
            // this.isStageDraft &&
            !this.isTournamentFieldDisabled('swap_players') &&
            !this.isCompetitionFieldDisabled(
              'swap_players',
              this.currentTournamentBlock
            ) &&
            this.$route.params.constructorMode === CONSTRUCTOR_MODES.PUBLISH &&
            specificProps.props.canSwapPlayers,
        };
      }
      if (specificProps.on) {
        matchProps.on = {
          ...matchProps.on,
          ...specificProps.on,
        };
      }
      return matchProps;
    },
    openMatchSchedulingModal(props) {
      this.openModal({
        name: 'MatchSchedulingModal',
        props,
        events: {
          save: () => this.updateMatchesData(),
        },
        settings: {
          clickOnBackdrop: false,
          variant: 'fit-content',
        },
      });
    },
    confirmStartRound(data) {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_start-round-message',
            'Now all battles in scheduled status will be started. Do you really want to proceed?'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.startRound.bind(null, data),
        },
      });
    },
    confirmFinishRound(data) {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_finish-round-message',
            'Now all NOT COMPLETED matches will be closed. Matches  without results will be cancelled, for matches with results the system will define a winner'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.finishRound.bind(null, data),
        },
      });
    },

    confirmResetMatch(data) {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_restart-confirm-message',
            'Now this match will be reset. Do you really want to proceed?'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.onMatchReset.bind(null, data),
        },
      });
    },
    confirmStartMatch(data) {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_start-confirm-message',
            'Now this match will be started. Do you really want to proceed?'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.onMatchStart.bind(null, data),
        },
      });
    },
    confirmFinishMatch(data) {
      this.openModal({
        name: 'HeraConfirm',
        props: {
          text: this.$t(
            '_web_tournaments-tableview_finish-confirm-message',
            'Now this match will be finished. Do you really want to proceed?'
          ),
        },
        events: {
          cancel: this.closeModal,
          confirm: this.onMatchFinish.bind(null, data),
        },
      });
    },
    openInviteModal({ player }) {
      this.openModal({
        name: 'InviteToTournament',
        props: {
          player,
          playerType: this.isSingle ? 'profile' : 'clan',
        },
        events: {
          close: this.closeModal,
        },
        settings: {
          variant: 'long-list',
        },
      });
    },

    onOpenUserMenu(event, player, match) {
      this.popoverItems = this.userMenuItems;
      this.optionsPosition = event.target.closest('.card');
      this.optionsData = { player, match };
    },
    getMatchActions(match) {
      return this.matchMenuItems
        .map(item => {
          let newItem = this.$lodash.cloneDeep(item);
          const isPublishRoute =
            this.$route.params.constructorMode === CONSTRUCTOR_MODES.PUBLISH;
          if (item.key === 'view-results') {
            newItem.disabled = !match.battle_id;
          }
          if (item.key === 'edit-results') {
            newItem = {
              ...newItem,
              key: this.isMatchConflicted(match) ? 'judging' : newItem.key,
              title: this.isMatchConflicted(match) ? 'Judging' : newItem.title,
              disabled: !this.canEditMatchResult(match) || isPublishRoute,
            };
          }
          if (
            item.key === 'tech-win-player-1' ||
            item.key === 'tech-win-player-2'
          ) {
            const playerIndex = +item.key.substr(-1, 1); // 1 or 2
            const key = `player_${playerIndex}`;
            const player = match[key] || {};
            const name = displayName(player) || key;
            newItem = {
              ...newItem,
              title: 'Set Tech win {participant}',
              handler: this.setTechWinner.bind(
                this,
                match.id,
                playerIndex,
                match.round,
                match.index
              ),
              props: {
                participant: name,
              },
              disabled: !this.canSetTieResult(match),
            };
          }

          if (item.key === 'force-start') {
            newItem.disabled = !this.canStartMatch(match) || isPublishRoute;
          }
          if (item.key === 'force-finish') {
            newItem.disabled = !this.canFinishMatch(match) || isPublishRoute;
          }
          if (item.key === 'force-restart' || item.key === 'force-reset') {
            newItem.disabled = !this.canRestartMatch(match) || isPublishRoute;
          }
          return newItem;
        })
        .filter(
          ({ key }) =>
            !(
              this.isBattleRoyale &&
              [
                'view-results',
                'tech-win-player-1',
                'tech-win-player-2',
              ].includes(key)
            )
        );
    },
    toggleMatchMenu(event, options = null) {
      const closest = event.target.closest('.playhera-icon');

      if (closest === this.optionsPosition) {
        return (this.optionsPosition = null);
      }

      const match = this.$lodash.cloneDeep(options);
      // const { id: matchID, roundIndex, matchIndex } = match;
      this.isMenuInHeader = false;
      this.stickyZIndex = 1000; // sticky header should be over popover
      this.popoverItems = this.getMatchActions(match);
      this.optionsPosition = closest;
      this.optionsData = match;
    },
    setTechWinner(matchID, playerIndex, roundIndex, matchIndex) {
      const match = this.$lodash.get(
        this.matches,
        `[${roundIndex}][${matchIndex}]`
      );
      match.isLoading = true;
      this.forceUpdate = true;
      this.setTechWin({ matchID, index: playerIndex })
        .then(this.fetchMatchesData)
        .finally(() => {
          this.forceUpdate = false;
          match.isLoading = false;
          this.setActiveMatchConfig();
        });
    },
    openRoundMenu(event, options = null) {
      this.isMenuInHeader = true;
      this.stickyZIndex = 990; // sticky header should be beneath popover
      this.popoverItems = this.roundMenuItems.map(el =>
        el.key === 'force-start-all'
          ? {
              ...el,
              disabled:
                this.currentTournamentBlock.status ===
                  COMPETITION_STATUSES.DRAFT ||
                this.isCompetitionFieldDisabled(
                  'round_start',
                  this.currentTournamentBlock
                ) ||
                this.currentTournamentStage.status === STAGE_STATUSES.DRAFT ||
                this.isStageFieldDisabled(
                  'round_start',
                  this.currentTournamentStage
                ),
            }
          : el.key === 'force-finish-all'
          ? {
              ...el,
              disabled:
                (this.currentTournamentBlock &&
                  this.currentTournamentBlock.status !==
                    COMPETITION_STATUSES.STARTED) ||
                (this.currentTournamentStage &&
                  this.currentTournamentStage.status !==
                    STAGE_STATUSES.STARTED),
            }
          : el
      );
      this.optionsPosition = event.target;
      this.optionsData = this.$lodash.cloneDeep(options);
    },
    executeMenuItemHandler(item) {
      if (!item.disabled) {
        item.handler({
          ...item,
          ...item.defaultOptions,
          ...this.optionsData,
          ...(this.grid ? { grid: this.grid } : null),
        });
      }
    },
    getMenuItemVariant(item) {
      return item.disabled ? 'muted' : item.variant || 'primary';
    },
    getMenuItemFill(item) {
      return this.$scss[
        `${item.disabled ? 'disabled' : item.variant || 'text'}Color`
      ];
    },
    startOneToOneChat({ player }) {
      this.startTournamentChat(player).catch(this.errorNotify);
    },
    openChat({
      conversationType,
      targetGroup,
      id: matchID,
      battle_id,
      grid,
      round: roundIndex,
    }) {
      this.sliderChatToggleLoader(true);
      this.toggleSliderChat();
      const options = {
        tournamentId: this.tournamentID,
        round: isFinite(roundIndex) ? roundIndex : this.optionsData.roundID,
        grid, // grid: this.grid
        conversationType,
        targetGroup,
        captainsOnly: true,
        visible: false,
        blockId: this.currentTournamentBlock.id,
      };
      if (targetGroup === 'all') {
        options.matchId = matchID;
      }
      this.createTournamentConversationBySignature(options)
        // .then(this.openConversationInSliderChat)
        .then(conversation =>
          this.openConversationInSliderChatEditor({
            conversation,
            predefinedTitle:
              matchID && !this.isBattleRoyale
                ? this.$t('_web_match_chat_title', `Match Id: {match} `, {
                    match: battle_id,
                  })
                : `${this.roundName(
                    this.rounds,
                    options.round
                  )} (${targetGroup}) `,
            predefinedSubtitle: '',
          })
        )
        .catch(this.errorNotify)
        .finally(() => {
          this.sliderChatToggleLoader(false);
          this.unsetLoading();
        });
    },
    preventAddPlayerInterruption(vm) {
      // debugger;
      if (this.isSwiss && this.isAddingPlayer && this.needToPickPlayer) {
        vm.preventClose = true;
        this.errorNotify({
          inModal: true,
          message: this.$t(
            '_web_errors_swiss_no_bye_player',
            "Swiss system doesn't support BYE player yet. You have to select a player from the provided list"
          ),
        });
      }
    },
    onMenuToggle(isVisible) {
      if (!isVisible) {
        this.optionsPosition = null;
        this.isAddingPlayer = false;
        this.needToPickPlayer = false;
      }
    },
    onActiveMatchToggle(isVisible) {
      if (!isVisible) {
        this.activeMatchPosition = null;
        this.activeMatchID = null;
        this.activeMatchIndex = null;
        this.activeMatchRoundIndex = null;
      }
    },
    onScrollStop({ offsetY }) {
      this.isHeadSticky = offsetY > 0;
      if (this.isHeadSticky) {
        if (this.$refs.popupMenu && this.$refs.popupMenu.position) {
          this.$refs.popupMenu.$forceUpdate();
        }
      }
      let newFirstVisibleMatch = Math.ceil(
        offsetY /
          this.getRoundItemHeight(this.roundItemHeight, this.rounds.length)(
            this.firstVisibleRound
          )
      );

      // 0/0 = NaN !
      if (isNaN(newFirstVisibleMatch)) {
        newFirstVisibleMatch = 0;
      }

      if (newFirstVisibleMatch !== this.firstVisibleMatch) {
        this.firstVisibleMatch = newFirstVisibleMatch;
        this.fetchMatchesData();
      }
    },
    onStartSwap(roundIndex, matchIndex, playerIndex) {
      this.setSwapFrom({
        roundIndex,
        matchIndex,
        playerIndex,
        matchId: this.matches[roundIndex][matchIndex].id,
        player: this.getPlayerBackup(roundIndex, matchIndex, playerIndex),
        grid: this.grid,
      });
      this.setIsSwapping(true);
    },
    onPlayerPickedForAdding(player) {
      this.needToPickPlayer = false;
      const isSamePlayerAsBackup =
        this.playerBackup &&
        (player.user_id === this.playerBackup.id ||
          player.user_id === this.playerBackup.login ||
          (this.playerBackup.player &&
            player.user_id === this.playerBackup.player.id) ||
          (this.playerBackup.player &&
            player.user_id === this.playerBackup.player.login));
      const { playerIndex, roundIndex, matchIndex, matchID } = this.optionsData;
      if (this.isSwiss && isSamePlayerAsBackup) {
        if (this.matches[roundIndex][matchIndex].players) {
          this.matches[roundIndex][matchIndex].players.splice(
            playerIndex,
            0,
            this.playerBackup
          );
          this.$set(this.matches[roundIndex][matchIndex], 'players', [
            ...this.matches[roundIndex][matchIndex].players,
          ]);
        } else {
          this.updatePlayerInMatch(
            roundIndex,
            matchIndex,
            playerIndex,
            this.playerBackup
          );
        }
        this.isAddingPlayer = false;
        this.optionsPosition = null;
        this.optionsData = null;
        this.$refs.grid.forceRender();
        return;
      }
      const emptyPlayer = this.getEmptyPlayer(playerIndex);
      const newPlayer = {
        ...emptyPlayer,
        [`player_${playerIndex}`]: {
          ...player,
          [player.player_type === 'profile' ? 'login' : 'id']: player.user_id,
          type: player.player_type,
          display_name: player.user_name,
        },
      };
      if (this.matches[roundIndex][matchIndex].players) {
        this.matches[roundIndex][matchIndex].players.push({
          profile: newPlayer[`player_${playerIndex}`],
          score: {},
        });
        this.$set(this.matches[roundIndex][matchIndex], 'players', [
          ...this.matches[roundIndex][matchIndex].players,
        ]);
      } else {
        this.$set(this.matches[roundIndex], matchIndex, {
          ...this.matches[roundIndex][matchIndex],
          [`player_${playerIndex}`]: newPlayer[`player_${playerIndex}`],
          [`player_empty_${playerIndex}`]: false,
          [`player_${playerIndex}_ready`]: true,
          [`captain_${playerIndex}`]: player.user_id,
        });
      }
      this.addPlayerToMatch({
        playerID: player.user_id,
        matchID: matchID,
        playerIndex: this.isBattleRoyale ? 0 : playerIndex,
      })
        .catch(error => {
          this.errorNotify(error);
          if (this.matches[roundIndex][matchIndex].players) {
            this.matches[roundIndex][matchIndex].players.pop();
            this.$set(this.matches[roundIndex][matchIndex], 'players', [
              ...this.matches[roundIndex][matchIndex].players,
            ]);
          } else {
            this.$set(this.matches[roundIndex], matchIndex, {
              ...this.matches[roundIndex][matchIndex],
              ...emptyPlayer,
            });
          }
        })
        .finally(() => {
          this.isAddingPlayer = false;
          this.optionsPosition = null;
          this.optionsData = null;
          this.$refs.grid.forceRender();
        });
    },
    onAddNewPlayer({
      event,
      roundIndex,
      matchIndex,
      playerIndex,
      playerID,
      matchID,
    } = {}) {
      this.isAddingPlayer = true;
      this.needToPickPlayer = this.isSwiss;
      this.addPlayerSearch = '';
      this.fetchPlayersForAdding(matchID)
        .then(players => {
          const justDeletedPlayer = this.playerBackup
            ? this.playerBackup.player || this.playerBackup
            : {};
          this.popoverItems = this.isSwiss
            ? [
                {
                  ...justDeletedPlayer,
                  captain: this.playerBackup ? this.playerBackup.captain : '',
                  player_type: justDeletedPlayer.type,
                  user_id: justDeletedPlayer.id || justDeletedPlayer.login,
                  user_name: justDeletedPlayer.display_name,
                },
                ...players,
              ]
            : players;
          this.optionsData = {
            roundIndex,
            matchIndex,
            playerIndex,
            playerID,
            matchID,
          };
          this.optionsPosition = event.target.closest('.ph-panel');
        })
        .catch(this.errorNotify);
    },
    onDeletePlayer({
      event,
      roundIndex,
      matchIndex,
      playerIndex,
      playerID,
      matchID,
    } = {}) {
      this.playerBackup = this.getPlayerBackup(
        roundIndex,
        matchIndex,
        playerIndex
      );
      const emptyPlayer = this.getEmptyPlayer(playerIndex);
      if (this.matches[roundIndex][matchIndex].players) {
        this.matches[roundIndex][matchIndex].players.splice(playerIndex, 1);
        this.$set(this.matches[roundIndex][matchIndex], 'players', [
          ...this.matches[roundIndex][matchIndex].players,
        ]);
      } else {
        this.$set(this.matches[roundIndex], matchIndex, {
          ...this.matches[roundIndex][matchIndex],
          ...emptyPlayer,
        });
      }
      if (this.isSwiss) {
        this.$refs.grid.forceRender();
        this.onAddNewPlayer({
          event: {
            ...event,
            target: event.target.closest('.ph-panel'),
          },
          roundIndex,
          matchIndex,
          playerIndex,
          playerID,
          matchID,
        });
        return;
      }
      this.deletePlayerFromMatch({ matchID, playerID })
        .catch(error => {
          this.errorNotify(error);
          if (this.matches[roundIndex][matchIndex].players) {
            this.matches[roundIndex][matchIndex].players.splice(
              playerIndex,
              0,
              this.playerBackup
            );
            this.$set(this.matches[roundIndex][matchIndex], 'players', [
              ...this.matches[roundIndex][matchIndex].players,
            ]);
          } else {
            this.updatePlayerInMatch(
              roundIndex,
              matchIndex,
              playerIndex,
              this.playerBackup
            );
          }
        })
        .finally(() => {
          this.$refs.grid.forceRender();
        });
    },
    getPlayerBackup(roundIndex, matchIndex, playerIndex) {
      const match = this.$lodash.get(
        this.matches,
        `[${roundIndex}][${matchIndex}]`,
        null
      );
      return !match
        ? this.getEmptyPlayer(playerIndex)
        : this.matches.players
        ? match.players[playerIndex]
        : {
            player: this.$lodash.cloneDeep(
              this.matches[roundIndex][matchIndex][`player_${playerIndex}`]
            ),
            player_ready: this.matches[roundIndex][matchIndex][
              `player_${playerIndex}_ready`
            ],
            player_result: this.matches[roundIndex][matchIndex][
              `player_${playerIndex}_result`
            ],
            player_score: this.matches[roundIndex][matchIndex][
              `player_${playerIndex}_score`
            ],
            player_empty: this.matches[roundIndex][matchIndex][
              `player_empty_${playerIndex}`
            ],
            captain: this.matches[roundIndex][matchIndex][
              `captain_${playerIndex}`
            ],
          };
    },
    getEmptyPlayer(playerIndex) {
      return {
        [`player_${playerIndex}`]: null,
        [`player_${playerIndex}_ready`]: false,
        [`player_${playerIndex}_result`]: 'none',
        [`player_${playerIndex}_score`]: 0,
        [`captain_${playerIndex}`]: null,
        [`player_empty_${playerIndex}`]: true,
      };
    },
    onEndSwap(roundIndex, matchIndex, playerIndex) {
      this.setSwapTo({
        roundIndex,
        matchIndex,
        playerIndex,
        grid: this.grid,
        player: this.getPlayerBackup(roundIndex, matchIndex, playerIndex),
      });
      const matchTo = { ...this.matches[roundIndex][matchIndex] };
      const playerTo = this.swapData.to.player;
      if (this.swapData.from.grid === this.grid) {
        this.updatePlayerInMatch(
          this.swapData.from.roundIndex,
          this.swapData.from.matchIndex,
          this.swapData.from.playerIndex,
          playerTo
        );
      }
      this.updatePlayerInMatch(
        roundIndex,
        matchIndex,
        playerIndex,
        this.swapData.from.player
      );
      this.swapPlayers({
        competitionID: this.competitionID,
        players: {
          match_from_id: this.swapData.from.matchId,
          match_to_id: matchTo.id,
          player_from_id:
            this.$lodash.get(this.swapData.from.player, `player.login`, '') ||
            this.$lodash.get(this.swapData.from.player, `player.id`, '') ||
            this.$lodash.get(this.swapData.from.player, 'profile.login', '') ||
            this.$lodash.get(this.swapData.from.player, 'profile.id'),
          player_to_id:
            this.$lodash.get(playerTo, `player.login`, '') ||
            this.$lodash.get(playerTo, `player.id`, '') ||
            this.$lodash.get(playerTo, 'profile.login', '') ||
            this.$lodash.get(playerTo, 'profile.id'),
        },
      })
        .catch(error => {
          this.errorNotify(error);
          this.setHasErrorOnSwap(true);
          if (this.swapData.from.grid === this.grid) {
            this.updatePlayerInMatch(
              this.swapData.from.roundIndex,
              this.swapData.from.matchIndex,
              this.swapData.from.playerIndex,
              this.swapData.from.player
            );
          }
          this.updatePlayerInMatch(
            roundIndex,
            matchIndex,
            playerIndex,
            playerTo
          );
        })
        .finally(() => {
          this.onCancelSwap();
          this.$refs.grid.forceRender();
        });
    },
    updatePlayerInMatch(roundIndex, matchIndex, playerIndex, playerData) {
      const match = this.matches[roundIndex][matchIndex];
      if (match.players) {
        this.$set(
          this.matches[roundIndex][matchIndex].players,
          playerIndex,
          playerData
        );
      } else {
        this.$set(this.matches[roundIndex], matchIndex, {
          ...match,
          [`player_${playerIndex}`]: this.$lodash.cloneDeep(playerData.player),
          [`player_${playerIndex}_ready`]: playerData.player_ready,
          [`player_${playerIndex}_result`]: playerData.player_result,
          [`player_${playerIndex}_score`]: playerData.player_score,
          [`player_empty_${playerIndex}`]: playerData.player_empty,
          [`captain_${playerIndex}`]: playerData.captain,
        });
      }
    },
    onCancelSwap() {
      this.clearSwapData();
      this.setIsSwapping(false);
    },
    onMatchResultsEdited() {
      if (confirm(this.UPDATE_CONFIRM_MSG)) {
        this.updateMatchesData();
      }
    },
    openMatchResultsEdit(obj) {
      this.openMatchResultsView(obj);
      this.editResultsMode = true;
    },
    openMatchResultsView(data) {
      this.editResultsData = {
        ...data,
      };

      this.showResultsModal = !this.isBattleRoyale;
      this.editResultsMode = false;
    },
    roundVisibilityChanged(isVisible, entry) {
      const roundIndex = entry.target.dataset.roundIndex;
      this.rounds[roundIndex].isVisible = isVisible;
      if (isVisible) {
        this.roundsUpdateQuery.next(roundIndex);
      }
    },

    updateMatchesData() {
      this.forceUpdate = true;
      this.isLoading = true;
      return this.fetchMatchesData()
        .catch(this.errorNotify)
        .then(() => {
          this.forceUpdate = false;
          this.isLoading = false;
        });
    },

    fetchMatchesData() {
      let promises = [];
      let startMatchIndex = this.firstVisibleMatch;

      let from, to;
      const visibleRounds = this.visibleInstanceCount || this.rounds.length;
      const maxRoundsInCycle = Math.min(
        this.firstVisibleRound + visibleRounds,
        this.rounds.length
      );

      for (
        let index = this.firstVisibleRound;
        index < maxRoundsInCycle;
        index += 1
      ) {
        if (index > this.firstVisibleRound) {
          const match = this.matches[index][startMatchIndex];
          const nextIndex =
            match && match.id && !this.grid ? match.next_match_index : -1;
          startMatchIndex =
            nextIndex > -1
              ? nextIndex
              : this.getNextMatchIndex(startMatchIndex, index + 1);
        }

        const roundVisibleCount = this.getRoundVisibleCount(index);
        from = Math.ceil(Math.max(startMatchIndex - roundVisibleCount, 0));
        to = Math.ceil(
          Math.min(
            startMatchIndex +
              roundVisibleCount +
              this.getRoundCacheCount(index),
            this.$lodash.get(this.rounds, `[${index}].matches`, 1)
          )
        );
        promises.push(this.getMatches(index, from, to).catch(this.errorNotify));
      }
      this.setLoading();
      return Promise.all(promises)
        .then(() => {
          if (this.$refs.grid) {
            this.$refs.grid.forceRender();
          }
        })
        .finally(this.unsetLoading);
    },
    fetchRoundsData(isUpdating = false) {
      return this.getCompetitionRounds({
        grid: this.grid,
      }).then(rounds => {
        if (rounds.length === 0 && !this.isLowerGrid) {
          this.$emit('noDataToShow');
        } else if (rounds.length > 0 && !this.isLowerGrid) {
          this.$emit('notEmpty');
        }
        this.rounds = rounds.map((round, i) => ({
          ...this.rounds[i],
          ...round,
        }));
        if (!isUpdating) {
          this.matches = this.rounds.map(el =>
            Array.from({ length: el.matches }, () => ({}))
          );
        }
        this.getItemHeight = this.getRoundItemHeight(
          this.roundItemHeight,
          rounds.length
        );
      });
    },
    getMatches(round, from = 0, to = this.pageSize, grid = this.grid) {
      const matchesPart = this.matches[round]
        ? this.matches[round].slice(from, to + 1).map(match => match.id)
        : [];
      const updateMatches = (matches, predicate) => {
        let tempMatches = this.$lodash.cloneDeep(this.matches[round]);
        tempMatches.splice(from, matches.length, ...matches.map(predicate));
        this.matches[round] = tempMatches;
        return matches;
      };
      return this.forceUpdate || matchesPart.some(id => !id)
        ? this.getCompetitionMatches({
            competitionID: this.competitionID,
            queryParams: { round, from, to, ...(grid ? { grid } : null) },
          })
            .then(matches =>
              updateMatches(matches, match => ({ ...match, isLoading: false }))
            )
            .then(matches => {
              return !this.needCheckInInfo
                ? matches
                : {
                    matches,
                    player_ids: matches
                      .reduce((acc, match) => {
                        const players = match.players
                          ? match.players.map(item => item.profile)
                          : [match.player_1, match.player_2];
                        return [...acc, ...players];
                      }, [])
                      .filter(player => player)
                      .map(player => player.login || player.id),
                  };
            })
            .then(({ matches, player_ids }) =>
              !this.needCheckInInfo
                ? Promise.resolve({ matches, players: [] })
                : this.fetchTournamentPlayers({
                    id: this.tournamentID,
                    query: {
                      player_ids,
                    },
                  }).then(response => ({
                    matches,
                    players: response.items,
                  }))
            )
            .then(({ matches, players }) => {
              if (this.needCheckInInfo) {
                this.checkInStatuses = {
                  ...this.checkInStatuses,
                  ...players.reduce(
                    (acc, player) => ({
                      ...acc,
                      [player.id]: player.fields_data.checked_in,
                    }),
                    {}
                  ),
                };
              }
              return matches;
            })
            .then(matches => {
              if (this.needCheckInInfo) {
                updateMatches(matches, match => {
                  const matchCopy = this.$lodash.cloneDeep(match);
                  if (Array.isArray(matchCopy.players)) {
                    matchCopy.players = matchCopy.players.map(player => ({
                      ...player,
                      profile: player.profile
                        ? {
                            ...player.profile,
                            checked_in: this.checkInStatuses[
                              player.profile.login || player.profile.id
                            ],
                          }
                        : player.profile,
                    }));
                  } else {
                    for (let i = 1; i < 3; i += 1) {
                      matchCopy[`player_${i}`] = matchCopy[`player_${i}`]
                        ? {
                            ...matchCopy[`player_${i}`],
                            checked_in: this.checkInStatuses[
                              matchCopy[`player_${i}`].login ||
                                matchCopy[`player_${i}`].id
                            ],
                          }
                        : matchCopy[`player_${i}`];
                    }
                  }
                  return matchCopy;
                });
              }
            })
        : Promise.resolve();
    },
    saveEditResults(match) {
      this.showResultsModal = false;
      this.savingResults = match.round;
      if (!match) {
        return;
      }
      this.saveResults(match, this.isBattleRoyale)
        .then(this.updateMatchesData)
        .then(() => {
          this.editResultsMode = false;
        })
        .catch(this.errorNotify)
        .finally(() => {
          this.savingResults = null;
        });
    },
    resetEditResults() {
      this.showResultsModal = false;
      this.editResultsMode = false;
      this.editResultsData = {}; // reset playerTable for battleRoyale
      this.savingResults = null;
    },

    onMatchStart(obj) {
      this.startMatch(obj)
        .then(this.updateMatchesData)
        .then(this.setActiveMatchConfig);
    },
    onMatchFinish(obj) {
      this.finishMatch(obj)
        .then(this.updateMatchesData)
        .then(this.setActiveMatchConfig);
    },
    onMatchReset(obj) {
      this.resetMatch(obj)
        .then(this.updateMatchesData)
        .then(this.setActiveMatchConfig);
    },
    setActiveCard(event, { matchIndex, roundIndex, id }) {
      if (this.activeMatchID !== id) {
        this.activeMatchIndex = matchIndex;
        this.activeMatchRoundIndex = roundIndex;
        this.activeMatchPosition = event.target.closest('.match-card');
        this.activeMatchID = id;
      } else {
        return;
      }
      this.setActiveMatchConfig();
    },
  },
};
</script>

<style lang="scss">
body.has-scrollbar {
  overflow-y: scroll !important;
}

#app > .playerTournamentBracket {
  .main-nav-wrapper,
  .stage-header {
    display: none !important;
  }
  .main-content-wrapper,
  main .content-wrapper {
    max-width: 100% !important;
  }
}

@import '~@/assets/scss/components/grid-binds.scss'; // use variables
.tournament-grid-wrapper {
  &.is-upper {
    $scrollbar-height: 20px; // scrollbar height = 4, default height ~20
    margin-bottom: -$scrollbar-height;
    .grid-table-body::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: $ph-bg;
      height: $scrollbar-height;
    }
  }

  .grid-table-header-wrapper {
    padding: 0 20px;
    top: 0;
    z-index: 3;
    overflow: hidden !important;
    background-color: $ph-card;
    &.is-invisible {
      height: 0 !important;
    }
  }
  .scroll-sync-container {
    width: 100%;
    overflow: auto hidden !important;
  }

  .grid-table-header {
    .round-step {
      &:first-child {
        > div {
          padding-left: 0 !important;
        }
      }
      &:last-child {
        > div {
          @include auto-rtl(padding-right, 0 !important);
        }
        @include auto-rtl(border-right, unset !important);
      }
      .card {
        min-width: 320px;
      }

      .round-menu-wrapper {
        min-width: auto;

        &:hover {
          path {
            fill: $white !important;
          }
        }
      }
    }
  }
  .round-step {
    min-width: 322px;
  }
  .matches-in-the-round {
    &.not-visible {
      .match-wrapper {
        display: none !important;
      }
    }
  }
  // .active-match-wrapper {
  //   // --translate-x: -50%;
  //   // --translate-y: -100%;
  // }
}

.grid-table-header,
.grid-table-body {
  .border-right {
    border-color: rgba(137, 139, 155, 0.2) !important;
    border-right-style: dashed !important;
  }
  .border-right {
    &:last-child {
      border: none !important;
    }
  }
}

// @olsy: border added for last round in lower-grid for joiner with upper-grid
.is-lower .grid-table-header > .is-empty:first-of-type {
  // .border-right
  & ~ .is-empty {
    &::before {
      content: '';
      position: absolute;
      top: -$line-width;
      bottom: -$line-width;
      left: -$line-width;
      width: $line-width;
      background-color: $line-color;
    }
    & ~ .is-empty::before {
      content: none;
    }
  }
}
</style>
